import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { LoginCredentials } from '../types';
import styled from 'styled-components';
import { Eye, EyeOff, User, Building2, ArrowLeft } from 'lucide-react';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #00d4aa;
  }
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 8px;
  color: #333;
  font-size: 28px;
`;

const Subtitle = styled.p`
  text-align: center;
  color: #666;
  margin-bottom: 32px;
  font-size: 14px;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 24px;
  border-radius: 8px;
  background: #f8f9fa;
  padding: 4px;
  gap: 4px;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#00d4aa' : '#666'};
  box-shadow: ${props => props.$active ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 16px;
  border: 2px solid ${props => props.$hasError ? '#dc3545' : '#e9ecef'};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  transition: color 0.2s ease;

  &:hover {
    color: #495057;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: #00d4aa;
  color: white;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #00b894;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
  text-align: center;
`;

const ForgotPasswordLink = styled(Link)`
  text-align: right;
  color: #00d4aa;
  font-size: 14px;
  font-weight: 500;
  margin-top: -8px;
  display: block;

  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
  color: #666;
  font-size: 14px;
`;

const FooterLink = styled(Link)`
  color: #00d4aa;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

export const LoginPage: React.FC = () => {
  const [loginType, setLoginType] = useState<'owner' | 'employee'>('owner');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const { login, employeeLogin, isAuthenticated, isAdmin, isSuperAdmin } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginCredentials>();

  useEffect(() => {
    if (shouldRedirect && isAuthenticated) {
      const redirectParam = searchParams.get('redirect');
      const safeRedirect = redirectParam && redirectParam.startsWith('/') ? redirectParam : null;

      if (safeRedirect && loginType === 'owner') {
        navigate(safeRedirect, { replace: true });
        return;
      }

      // Redirecionar para painel de admin se for admin
      if (isAdmin || isSuperAdmin) {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [shouldRedirect, isAuthenticated, isAdmin, isSuperAdmin, navigate, searchParams, loginType]);

  const onSubmit = async (data: LoginCredentials) => {
    setIsLoading(true);

    try {
      if (loginType === 'owner') {
        await login(data);
        showSuccess('Login realizado com sucesso!');
      } else {
        await employeeLogin(data);
        showSuccess('Login de funcionário realizado com sucesso!');
      }
      setShouldRedirect(true);
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao fazer login';
      showError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <BackButton type="button" onClick={() => navigate('/')}>
          <ArrowLeft size={16} />
          Voltar
        </BackButton>
        <Title>Bem-vindo</Title>
        <Subtitle>Faça login para acessar sua conta</Subtitle>

        <TabContainer>
          <Tab 
            type="button"
            $active={loginType === 'owner'} 
            onClick={() => setLoginType('owner')}
          >
            <Building2 size={16} />
            Proprietário
          </Tab>
          <Tab 
            type="button"
            $active={loginType === 'employee'} 
            onClick={() => setLoginType('employee')}
          >
            <User size={16} />
            Funcionário
          </Tab>
        </TabContainer>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <InputGroup>
            <Input
              type="email"
              placeholder="Email"
              $hasError={!!errors.email}
              {...register('email', {
                required: 'Email é obrigatório',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Email inválido'
                }
              })}
            />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Senha"
              $hasError={!!errors.password}
              {...register('password', {
                required: 'Senha é obrigatória',
                minLength: {
                  value: 6,
                  message: 'Senha deve ter pelo menos 6 caracteres'
                }
              })}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </InputGroup>

          <ForgotPasswordLink to="/forgot-password">
            Esqueceu a senha?
          </ForgotPasswordLink>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Entrando...' : 'Entrar'}
          </Button>
        </Form>

        {loginType === 'owner' && (
          <Footer>
            Não tem uma conta?{' '}
            <FooterLink to="/register">Cadastre-se aqui</FooterLink>
          </Footer>
        )}
      </LoginCard>
    </LoginContainer>
  );
};
